/**
 * What this account may do — one answer, shared, and never the authority.
 *
 * ## It only renders; the server decides
 *
 * Every capability here came from `/auth/me`, and every gated route on the
 * server checks again. This exists so a control can be greyed without a round
 * trip and so a locked feature can say *why*, not so the client can decide —
 * a list the browser was handed is a list the browser can edit. The same
 * division `api/me.ts` already states for `isAdmin`.
 *
 * ## Permissive is the default, in three places, on purpose
 *
 * No server configured, the fetch failing, and an older backend that has never
 * heard of entitlements all resolve to *everything allowed*. That is the same
 * bargain the whole app already strikes — `me.ts` degrades to `null` rather
 * than throwing, `journalEnabled()` is false when there is no server — and here
 * the cost of the other choice is total: a fetch that times out would black out
 * the picker, the group bar and the journal at once, with nothing on screen
 * explaining it, on exactly the deployments that have no way to fix it.
 *
 * A capability the user genuinely lacks is still refused, because the refusal
 * that matters is on the server.
 *
 * ## One load, not six
 *
 * `AppMenu`, `ActingAs`, `MobileMore` and `ThemeEditor` each call `fetchMe` for
 * themselves. That was fine while the answer was "am I an admin"; it is not
 * fine now that the answer decides what half the UI renders, because four
 * independent fetches resolve at four different moments and controls would pop
 * in raggedly. So this module holds the one copy and hands out subscriptions.
 */

import { useEffect, useState } from "react";

import { fetchMe, type Me } from "../api/me";
import { onAuthChanged } from "./session";
import { onActingAsChanged } from "./act-as";
import { onPassStale } from "./pass";

/** The resolved answer, or `null` while it is still being asked for. */
let held: Me | null = null;
let loaded = false;
let inFlight: Promise<Me | null> | null = null;

const listeners = new Set<(me: Me | null) => void>();

function publish(): void {
  for (const notify of listeners) notify(held);
}

/**
 * Fetch once, and share the promise.
 *
 * Concurrent callers get the same request rather than one each — four
 * components mounting in the same tick is the normal case, not the exception.
 */
export function load(): Promise<Me | null> {
  if (inFlight) return inFlight;
  inFlight = fetchMe()
    .then((found) => {
      held = found;
      loaded = true;
      publish();
      return found;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Ask again, because something that changes the answer has happened.
 *
 * Signing in or out, and switching the account being acted as — an admin
 * looking at a test user must see *that user's* entitlements, since the whole
 * point of impersonation is to reproduce what they see. The server already
 * resolves against the acted-as key; this is the client half of the same rule.
 */
// A response said this account's pass is stale — an admin changed something.
// Ask again **without** clearing what is held: the new answer replaces it when
// it lands, so controls do not flash to the permissive default in between.
onPassStale(() => void load());

export function refresh(): Promise<Me | null> {
  held = null;
  loaded = false;
  inFlight = null;
  publish();
  return load();
}

/**
 * Ask again **keeping** what is held — the pass-stale path, for a caller that
 * knows the answer moved but not who it belongs to: a payment or a portal
 * change landing by webhook.
 *
 * Not `refresh`: that clears `loaded`, and `PlanScreen` answers *not loaded*
 * with "Loading…" — unmounting `BillingSection`, whose remount polls again and
 * refreshes again. Back from Stripe that loop blinked the plan screen for as
 * long as it stayed open (2026-10-12).
 */
export function reread(): Promise<Me | null> {
  return load();
}

export function current(): Me | null {
  return held;
}

/** Whether the answer has arrived. `false` means *not yet*, never *nothing*. */
export function ready(): boolean {
  return loaded;
}

function permissive(me: Me | null): boolean {
  // No answer is permissive for the module docstring's reason: a fetch that
  // never resolves must not black out the app.
  return me === null || me.permissive;
}

/**
 * Whether `capability` is held.
 *
 * `domain:value`, matching the server's grammar. A grant of `ind:*` satisfies
 * `ind:Seasonality`, and `*` satisfies anything — the same three rules
 * `caps.matches` implements, because two implementations of one grammar drift
 * into a control that is greyed for a user the server would have allowed.
 */
export function can(capability: string, me: Me | null = held): boolean {
  if (permissive(me)) return true;
  const domain = capability.split(":", 1)[0];
  return (me as Me).capabilities.some(
    (granted) =>
      granted === capability || granted === "*" || granted === `${domain}:*`,
  );
}

/** The ceiling on a `lim:` capability. `null` means no ceiling, not zero. */
export function limitOf(capability: string, me: Me | null = held): number | null {
  if (permissive(me)) return null;
  const found = (me as Me).limits[capability];
  return typeof found === "number" ? found : null;
}

/**
 * Every value held in one domain, or `"*"` for all of them.
 *
 * What a picker filters on: `valuesIn("group")` is the set of universes this
 * account may select. Returns `"*"` rather than an expanded list because the
 * client has no catalogue to expand it against, and the caller has to handle
 * the wildcard either way.
 */
export function valuesIn(domain: string, me: Me | null = held): Set<string> | "*" {
  if (permissive(me)) return "*";
  const out = new Set<string>();
  for (const granted of (me as Me).capabilities) {
    if (granted === "*") return "*";
    const [head, ...rest] = granted.split(":");
    if (head !== domain || rest.length === 0) continue;
    const value = rest.join(":");
    if (value === "*") return "*";
    out.add(value);
  }
  return out;
}

export function onChanged(callback: (me: Me | null) => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

// Re-ask whenever who-we-are changes. Registered at module load rather than in
// a component, so the answer is already in flight by the time anything renders
// and no single component owns the lifecycle of a shared value.
onAuthChanged(() => {
  void refresh();
});
onActingAsChanged(() => {
  void refresh();
});

/**
 * The hook. `me` is `null` until the answer arrives, and `can` is permissive
 * until then — so nothing flashes locked and then unlocks, which reads as the
 * app taking a feature away.
 */
export function useEntitlements(): {
  me: Me | null;
  ready: boolean;
  can: (capability: string) => boolean;
  limitOf: (capability: string) => number | null;
  valuesIn: (domain: string) => Set<string> | "*";
} {
  const [me, setMe] = useState<Me | null>(held);
  const [isReady, setReady] = useState(loaded);

  useEffect(() => {
    const stop = onChanged((next) => {
      setMe(next);
      setReady(loaded);
    });
    void load().then(() => setReady(loaded));
    return stop;
  }, []);

  return {
    me,
    ready: isReady,
    can: (capability) => can(capability, me),
    limitOf: (capability) => limitOf(capability, me),
    valuesIn: (domain) => valuesIn(domain, me),
  };
}
